import * as React from 'react';
import './Select.css';

// ------------------------------------------------------------------------------------------------
// component

interface SelectOption {
    label: string,
    id: string
}

interface Props {
    className?: string;
    options: SelectOption[],
    value: string[] | string,
    placeHolder?: string,
    name: string,
    highlight?: boolean,
    multiple?: boolean,
    onChange?: (name: string, value: string[] | string) => void;
}

interface State {
    showDropdown: boolean,
    buttonWidth: number | null,
    hoveredOptionIndex: number | null
}

class Select extends React.PureComponent<Props, State> {

    // --------------------------------------------------------------------------------------------
    // state

    public state = {
        showDropdown: false,
        buttonWidth: null,
        hoveredOptionIndex: null
    };

    private buttonRef = React.createRef<HTMLButtonElement>();
    private optionListRef = React.createRef<HTMLDivElement>();

    // --------------------------------------------------------------------------------------------
    // lifecycle

    componentDidMount() {
        const buttonWidth = this.buttonRef.current.offsetWidth;
        this.setState({ buttonWidth });
        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleKeyPress);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeyPress);
    }


    // --------------------------------------------------------------------------------------------
    // actions

    handleButtonClick = () => {
        this.setState({
            showDropdown: !this.state.showDropdown
        });
    }

    handleSelect = (value: string) => {
        if (this.props.multiple) {
            const updatedValues = Array.isArray(this.props.value) ? [...this.props.value] : [];
            const index = updatedValues.indexOf(value);

            if (index !== -1) {
                updatedValues.splice(index, 1);
            } else {
                updatedValues.push(value);
            }

            this.props.onChange(this.props.name, updatedValues);
        } else {
            let updatedValue = this.props.value;
            if (value === updatedValue) {
                updatedValue = '';
            } else {
                updatedValue = value;
            }
            this.props.onChange(this.props.name, updatedValue);
            this.setState({ showDropdown: false, hoveredOptionIndex: null });
        }
    }

    handleOutsideClick = (event: MouseEvent) => {
        if (this.state.showDropdown) {
            if (this.optionListRef.current && !this.optionListRef.current.contains(event.target as Node) && !this.buttonRef.current.contains(event.target as Node)) {
                this.setState({ showDropdown: false, hoveredOptionIndex: null });
            }
        }
    }

    handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown' && this.state.showDropdown) {
            event.preventDefault();
            const optionsCount = this.props.options.length;
            const currentIndex = this.state.hoveredOptionIndex !== null ? this.state.hoveredOptionIndex : -1;
            const nextIndex = (currentIndex + 1) % optionsCount;
            this.setState({ hoveredOptionIndex: nextIndex }, () => {
                this.scrollHoveredOptionIntoView();
            });
        } else if (event.key === 'ArrowUp' && this.state.showDropdown) {
            event.preventDefault();
            const optionsCount = this.props.options.length;
            const currentIndex = this.state.hoveredOptionIndex !== null ? this.state.hoveredOptionIndex : optionsCount;
            const prevIndex = (currentIndex - 1 + optionsCount) % optionsCount;
            this.setState({ hoveredOptionIndex: prevIndex }, () => {
                this.scrollHoveredOptionIntoView();
            });
        } else if (event.key === 'Enter' && this.state.showDropdown && this.state.hoveredOptionIndex !== null) {
            event.preventDefault();
            const selectedOption = this.props.options[this.state.hoveredOptionIndex];
            this.handleSelect(selectedOption.id);
        }
    }

    scrollHoveredOptionIntoView = () => {
        if (this.optionListRef.current && this.state.hoveredOptionIndex !== null) {
            const hoveredOptionElement = this.optionListRef.current.querySelector(`li:nth-child(${this.state.hoveredOptionIndex + 1})`);
            if (hoveredOptionElement) {
                hoveredOptionElement.scrollIntoView({
                    behavior: 'auto',
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        }
    }

    // --------------------------------------------------------------------------------------------
    // render

    public render() {
        let containerClass = "select-dropdown";
        if (this.props.className) {
            containerClass = `${this.props.className} ${containerClass}`;
        }

        const { buttonWidth } = this.state;

        return (
            <div className={containerClass}>
                <button
                    className={`select-button ${(this.props.highlight && this.props.value.length === 0) && "highlight"}`}
                    onClick={this.handleButtonClick}
                    ref={this.buttonRef}
                >
                    {this.props.value.length === 0 &&
                        <div className='placeholder'>{this.props.placeHolder}</div>}

                    <div>
                        {Array.isArray(this.props.value) ?
                            this.props.value.map(id => {
                                const option = this.props.options.find(option => option.id === id);
                                return option ? option.label : id;
                            }).join(", ") : (
                                this.props.options.find(option => option.id === this.props.value) ?
                                    this.props.options.find(option => option.id === this.props.value).label :
                                    this.props.value
                            )}
                    </div>

                    <div>
                        {this.state.showDropdown ? "⏶" : "⏷"}
                    </div>
                </button>

                <div
                    className='select-option-list'
                    ref={this.optionListRef}
                    style={{ width: buttonWidth ? `${buttonWidth}px` : 'auto' }}
                >
                    {this.props.options.length > 0 && this.state.showDropdown ?
                        <ul>
                            {this.props.options.map((item, index) =>

                                <li key={item.label}
                                    onClick={() => { this.handleSelect(item.id) }}
                                    className={`${this.state.hoveredOptionIndex === index && "hovered"}   ${this.props.value.includes(item.id) && "select-option-list-selected"} `}>
                                    <div className='select-option-list-checkIcon'>
                                        {this.props.value.includes(item.id) ? "√" : ""}
                                    </div>
                                    {item.label}
                                </li>

                            )}
                        </ul>

                        : null
                    }
                </div>
            </div>
        );
    }
}

export default Select;
